/**
 * Settings › Roles & permissions — `organization-build-book.md` §6.6,
 * `org-06-roles-permissions.png`.
 *
 * A role is a set of permission levels and nothing else. **Where** it applies
 * is granted per person under Users & roles — which is why this screen has no
 * branches on it at all, and why the sentence at the top says so: the two
 * halves of "access" used to be edited on one screen, and the result was
 * roles that quietly meant something different in every branch.
 *
 * The eighteen areas are grouped four ways. The grouping is layout, not a
 * second model — the API still enforces each area on its own — but eighteen
 * rows in one column is a list nobody reads to the bottom.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  PERMISSION_AREA_GROUPS, PERMISSION_AREA_INFO, PERMISSION_LEVELS, PERMISSION_LEVEL_INFO, can,
  type MeResponse, type PermissionLevel, type PermissionMatrix,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { Avatar, Button, EmptyState, Tag } from '../components/primitives.js'
import { ConfirmDialog, Modal, Popover } from '../components/overlays.js'
import { Field, SelectInput, TextInput } from '../components/form/index.js'
import { LevelPicker } from '../components/people/level-pill.js'
import { IconMore, IconPlus, IconSearch } from '../frame/icons.js'
import { useSettingsBar } from '../settings/settings-bar.js'
import { RolesActivityTable } from '../settings/activity-roles.js'

interface RoleRow {
  id: string
  name: string
  description: string
  defaultApp: string
  mfaRequired: boolean
  isSystem: boolean
  peopleCount: number
  version: number
}

interface RoleDetail extends RoleRow {
  permissions: PermissionMatrix
  people: { id: string; name: string; email: string }[]
}

/* The crew app is the only other landing; everyone else opens the office app, whose home is Activity. */
const APP_OPTIONS = [
  { value: 'office', label: 'Office app' },
  { value: 'crew_app', label: 'Crew app' },
]

export function Roles({ me }: { me: MeResponse }) {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [rows, setRows] = useState<RoleRow[]>([])
  const [role, setRole] = useState<RoleDetail | null>(null)
  const [draft, setDraft] = useState<RoleDetail | null>(null)
  const [query, setQuery] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [retiring, setRetiring] = useState(false)
  /*
   * §G.4: changing what a role can do is confirmed with a password, every
   * time. The server answers `step_up_required` rather than refusing, so this
   * is a prompt and a retry, not an error — which is why it holds the action
   * that was interrupted and runs it again once the password lands.
   */
  const [stepUp, setStepUp] = useState<{
    password: string; error: string | null; run: (password: string) => Promise<void>
  } | null>(null)

  /** Runs a write, and turns a step-up into a prompt that will retry it. */
  const guarded = useCallback(async (
    run: (password?: string) => Promise<void>, password?: string,
  ) => {
    setBusy(true)
    setError(null)
    try {
      if (password) await request('/auth/step-up', { method: 'POST', body: { password } })
      await run(password)
      setStepUp(null)
    } catch (problem) {
      const failure = problem as { code?: string; message?: string }
      if (failure.code === 'step_up_required') {
        setStepUp({ password: '', error: null, run: (next) => guarded(run, next) })
      } else if (failure.code === 'credentials_invalid') {
        setStepUp((was) => (was
          ? { ...was, password: '', error: failure.message ?? 'That password is not right.' }
          : was))
      } else {
        setError(failure.message ?? 'That did not work.')
      }
    } finally {
      setBusy(false)
    }
  }, [])

  const canEdit = can(me.permissions, 'roles_and_permissions', 'edit')
  const selectedId = params.get('role')

  const select = useCallback((id: string) => {
    setParams((was) => {
      const next = new URLSearchParams(was)
      next.set('role', id)
      return next
    }, { replace: true })
  }, [setParams])

  const load = useCallback(async () => {
    try {
      const { items } = await request<{ items: RoleRow[] }>('/roles')
      setRows(items)
      return items
    } catch (problem) {
      setError((problem as { message?: string }).message ?? 'The roles did not load.')
      return []
    }
  }, [])

  useEffect(() => { void load() }, [load])

  useEffect(() => {
    if (selectedId || rows.length === 0) return
    select(rows[0]!.id)
  }, [selectedId, rows, select])

  const reload = useCallback(async () => {
    if (!selectedId) { setRole(null); setDraft(null); return }
    try {
      const one = await request<RoleDetail>(`/roles/${selectedId}`)
      setRole(one)
      setDraft(one)
      setError(null)
    } catch (problem) {
      setError((problem as { message?: string }).message ?? 'That role did not load.')
    }
  }, [selectedId])

  useEffect(() => { void reload() }, [reload])

  const shown = useMemo(() => {
    const term = query.trim().toLowerCase()
    return term ? rows.filter((row) => row.name.toLowerCase().includes(term)) : rows
  }, [rows, query])

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(role),
    [draft, role])

  const barActions = useSettingsBar({
    title: 'Roles & permissions',
    sub: 'a role is a set of permission levels · where it applies is granted per person '
      + 'under Users & roles',
    crumbs: [{ label: 'Users & roles', to: '/settings/people' }],
    actions: (
      <>
        <Button className="h-8 text-[12.5px]" onClick={() => navigate('/settings/people')}>
          Back to people
        </Button>
        {canEdit && (
          <Button
            variant="primary"
            className="h-8 text-[12.5px]"
            onClick={() => void create()}
          >
            <IconPlus size={15} />New role
          </Button>
        )}
      </>
    ),
  })

  const duplicate = (fromId: string | null) => (fromId
    ? guarded(async () => {
      const made = await request<{ id: string }>(`/roles/${fromId}/duplicate`,
        { method: 'POST', body: {} })
      await load()
      select(made.id)
    })
    : Promise.resolve())

  /** A role that starts from nothing, for a company whose shape is its own. */
  const create = () => guarded(async () => {
    const made = await request<{ id: string }>('/roles', {
      method: 'POST',
      body: { name: 'New role', description: '', defaultApp: 'office', mfaRequired: true },
    })
    await load()
    select(made.id)
  })

  const save = () => (draft
    ? guarded(async () => {
      await request(`/roles/${draft.id}`, {
        method: 'PUT',
        body: {
          name: draft.name,
          description: draft.description,
          defaultApp: draft.defaultApp,
          mfaRequired: draft.mfaRequired,
          permissions: draft.permissions,
          version: draft.version,
        },
      })
      await load()
      await reload()
    })
    : Promise.resolve())

  const retire = () => (draft
    ? guarded(async () => {
      await request(`/roles/${draft.id}`, { method: 'DELETE' })
      const left = await load()
      if (left[0]) select(left[0].id)
    })
    : Promise.resolve())

  return (
    <div className="flex flex-col gap-5 p-5">
      {barActions}

      {/*
        * Activity's table (`docs/activity.md` §12.1, Board 17 A), above the
        * role list and the matrix, which are as they were.
        */}
      <RolesActivityTable canEdit={canEdit} />

      <div className="flex items-start gap-5 max-lg:flex-col">
      <div className={cn('flex w-[300px] shrink-0 flex-col self-start overflow-hidden max-lg:w-full',
        'rounded-record border border-line bg-surface')}>
        <div className="p-3 pb-2">
          <label className="relative block">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-faint">
              <IconSearch size={14} />
            </span>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Find a role"
              aria-label="Find a role"
              className={cn('h-[34px] w-full rounded-field border border-line bg-surface pl-8 pr-2.5',
                'text-cell text-text placeholder:text-text-faint',
                'focus:border-accent-line focus:outline-none focus:ring-2 focus:ring-accent-ring')}
            />
          </label>
        </div>

        <div className="flex flex-col pb-2">
          {shown.map((row) => (
            <button
              key={row.id}
              type="button"
              onClick={() => select(row.id)}
              className={cn('mx-2 flex items-center gap-2.5 rounded-[10px] px-3 py-[9px] text-left',
                row.id === selectedId
                  ? 'border border-accent-line bg-accent-soft'
                  : 'border border-transparent hover:bg-hover')}
            >
              <span aria-hidden className={cn('h-2 w-2 shrink-0 rounded-full',
                row.isSystem ? 'bg-accent' : 'bg-line-strong')} />
              <span className="flex min-w-0 flex-col">
                <span className="flex items-center gap-1.5">
                  <b className="truncate text-[13.5px] text-text">{row.name}</b>
                  {row.isSystem && <Tag>system</Tag>}
                </span>
                <span className="truncate text-[11.5px] text-text-muted">
                  {row.peopleCount} {row.peopleCount === 1 ? 'person' : 'people'}
                  {' · '}{APP_OPTIONS.find((one) => one.value === row.defaultApp)?.label}
                </span>
              </span>
            </button>
          ))}
        </div>

        {canEdit && selectedId && (
          <div className="flex gap-3 border-t border-line-faint px-4 py-2.5 text-[12.5px] font-semibold">
            <button type="button" className="text-accent" onClick={() => void duplicate(selectedId)}>
              Duplicate {rows.find((row) => row.id === selectedId)?.name}
            </button>
          </div>
        )}
      </div>

      {draft
        ? (
          <div className={cn('flex min-w-0 flex-1 flex-col self-start overflow-hidden',
            'max-w-[940px] rounded-record border border-line bg-surface',
            'max-lg:w-full max-lg:max-w-full')}>

            <div className="grid grid-cols-3 gap-3.5 px-[18px] pb-3 pt-4 max-sm:grid-cols-1">
              <Field label="Name" required>
                {({ id }) => (
                  <TextInput
                    id={id}
                    value={draft.name}
                    readOnly={draft.isSystem || !canEdit}
                    onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                  />
                )}
              </Field>

              <Field label="Opens in">
                {({ id }) => (
                  <SelectInput
                    id={id}
                    label="Opens in"
                    value={draft.defaultApp}
                    disabled={!canEdit}
                    onChange={(value) => value && setDraft({ ...draft, defaultApp: value })}
                    options={APP_OPTIONS}
                  />
                )}
              </Field>

              <Field label="Two-step sign-in">
                {({ id }) => (
                  <SelectInput
                    id={id}
                    label="Two-step sign-in"
                    value={draft.mfaRequired ? 'required' : 'optional'}
                    disabled={!canEdit}
                    onChange={(value) => setDraft({ ...draft, mfaRequired: value === 'required' })}
                    options={[
                      { value: 'required', label: 'Required' },
                      { value: 'optional', label: 'Optional' },
                    ]}
                  />
                )}
              </Field>
            </div>

            <div className="flex flex-wrap items-baseline gap-2 border-t border-line-faint px-[18px] pb-1.5 pt-3.5">
              <b className="text-[13.5px] text-text">What this role can do</b>
              <span className="text-[12px] text-text-muted">
                at every place it is granted · none hides the area entirely
              </span>
              <span className="ml-auto flex flex-wrap gap-x-3 text-[11.5px] text-text-faint">
                {PERMISSION_LEVELS.filter((level) => level !== 'none').map((level) => (
                  <span key={level}>
                    <b className="font-semibold capitalize text-text-muted">{level}</b>{' '}
                    {PERMISSION_LEVEL_INFO[level]}
                  </span>
                ))}
              </span>
            </div>

            {PERMISSION_AREA_GROUPS.map((group) => (
              <div key={group.title} className="flex flex-col">
                <span className={cn('px-[18px] pb-1 pt-3 text-[10.5px] font-semibold uppercase',
                  'tracking-[0.06em] text-text-faint')}>
                  {group.title}
                </span>
                {group.areas.map((area) => (
                  <div
                    key={area}
                    className="flex items-center gap-3 border-b border-line-faint px-[18px] py-2"
                  >
                    <span className="flex min-w-0 flex-1 flex-col">
                      <b className="truncate text-[13.5px] text-text">
                        {PERMISSION_AREA_INFO[area].label}
                      </b>
                      <span className="truncate text-[12px] text-text-muted">
                        {PERMISSION_AREA_INFO[area].detail}
                      </span>
                    </span>
                    <LevelPicker
                      label={PERMISSION_AREA_INFO[area].label}
                      value={draft.permissions[area]}
                      disabled={!canEdit}
                      onChange={(level: PermissionLevel) => setDraft({
                        ...draft,
                        permissions: { ...draft.permissions, [area]: level },
                      })}
                    />
                  </div>
                ))}
              </div>
            ))}

            {error && <p className="m-0 px-[18px] pt-2 text-[12.5px] text-danger">{error}</p>}

            <div className={cn('flex flex-wrap items-center gap-2.5 border-t border-line-faint',
              'bg-surface-alt px-[18px] py-3')}>
              <span className="flex flex-wrap items-center gap-1.5 text-[12px] text-text-faint">
                People with this role:
                {draft.people.length === 0 && <span>nobody yet</span>}
                {draft.people.slice(0, 6).map((person) => (
                  <span key={person.id} className="flex items-center gap-1">
                    <Avatar name={person.name} size={24} />
                    <span className="text-accent">{person.name}</span>
                  </span>
                ))}
                {draft.people.length > 6 && <span>and {draft.people.length - 6} more</span>}
              </span>

              <span className="ml-auto flex shrink-0 gap-2">
                {canEdit && !draft.isSystem && (
                  <RoleMenu onRetire={() => setRetiring(true)} onDuplicate={() => void duplicate(draft.id)} />
                )}
                <Button
                  className="h-8 text-[12.5px]"
                  disabled={!dirty || busy}
                  onClick={() => setDraft(role)}
                >
                  Discard
                </Button>
                <Button
                  variant="primary"
                  className="h-8 text-[12.5px]"
                  disabled={!canEdit || busy}
                  onClick={() => void save()}
                >
                  {busy ? 'Saving…' : 'Save role'}
                </Button>
              </span>
            </div>
          </div>
        )
        : (
          <div className="min-w-0 flex-1 rounded-record border border-line bg-surface">
            <EmptyState
              title={error ?? 'Choose a role'}
              body="A role is a set of levels. Where it applies is granted per person."
            />
          </div>
        )}
      </div>

      {stepUp && (
        <Modal
          open
          onClose={() => setStepUp(null)}
          title="Confirm your password"
          footer={(
            <>
              <Button onClick={() => setStepUp(null)}>Cancel</Button>
              <Button
                variant="primary"
                disabled={busy || !stepUp.password}
                onClick={() => void stepUp.run(stepUp.password)}
              >
                Confirm
              </Button>
            </>
          )}
          footerNote="It stays unlocked for ten minutes."
        >
          <p className="m-0 mb-3 text-body text-text-muted">
            Changing what a role can do needs your password again.
          </p>
          <input
            type="password"
            autoFocus
            value={stepUp.password}
            onChange={(event) => setStepUp({ ...stepUp, password: event.currentTarget.value, error: null })}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && stepUp.password) void stepUp.run(stepUp.password)
            }}
            className="w-full rounded-field border border-line bg-field px-3 py-2 text-body text-text"
          />
          {stepUp.error && <p className="m-0 mt-2 text-body text-danger">{stepUp.error}</p>}
        </Modal>
      )}

      {retiring && draft && (
        <ConfirmDialog
          title={`Retire ${draft.name}?`}
          confirmLabel="Retire role"
          onCancel={() => setRetiring(false)}
          onConfirm={() => { setRetiring(false); void retire() }}
          body={`${draft.name} stops being something you can grant. Everybody who has ever held `
            + 'it keeps their history, and the audit log still names it.'}
        />
      )}
    </div>
  )
}

function RoleMenu({ onRetire, onDuplicate }: { onRetire: () => void; onDuplicate: () => void }) {
  const [open, setOpen] = useState(false)
  const [at, setAt] = useState<HTMLElement | null>(null)

  return (
    <>
      <span ref={setAt} className="inline-flex">
        <Button
          aria-label="More for this role"
          className="h-8 w-8 p-0"
          onClick={() => setOpen((was) => !was)}
        >
          <IconMore size={16} />
        </Button>
      </span>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchor={at}
        align="right"
        width={186}
        label="More for this role"
      >
        <div className="flex flex-col py-1">
          <button
            type="button"
            onClick={() => { setOpen(false); onDuplicate() }}
            className="flex h-9 items-center px-3 text-left text-cell text-text hover:bg-hover"
          >
            Duplicate
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); onRetire() }}
            className="flex h-9 items-center px-3 text-left text-cell text-danger hover:bg-danger-bg"
          >
            Retire
          </button>
        </div>
      </Popover>
    </>
  )
}
